import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import BFFProxy from './bffProxy';
import axiosWithPayloadInterceptor from '../interceptor/axiosWithPayloadInterceptor';
import {
  URLS,
  HTTP_METHODS,
  FILE_TYPES,
  HTTP_STATUS,
  REQUEST_TYPES
} from '../helpers/constants';
import { S3_FILE_UPLOAD_ERROR, UNABLE_TO_PERSIST_RECALCULATION_COMMENT } from '../helpers/messageConstant';

/**
 * Fetch data from APIs related to agreements
 * @author skit1169
 */
class AgreementProxy {
  static async fetchAgreementSearchData(data) {
    const url = `${URLS.AGREEMENT_SEARCH_REQUEST_URL}`;
    const config = {
      url,
      method: HTTP_METHODS.POST,
      data
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchAgreementsSearchData(data) {
    const { houseTypes, vendors, businessCenters } = data;
    const url = `${URLS.AGREEMENTS_SEARCH_REQUEST_URL}`;
    const config = {
      url,
      method: HTTP_METHODS.GET,
      params: {
        houseTypes,
        vendors,
        businessCenters
      }
    };
    const response = await axiosWithPayloadInterceptor(config);
    return response.data;
  }

  static async fetchVendorBases() {
    const url = `${URLS.VENDOR_BASES_FETCH_URL}`;
    const config = {
      url,
      method: HTTP_METHODS.GET
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchBusinessCenters(data) {
    const url = `${URLS.BUSINESS_CENTERS_FETCH_URL}`;
    const config = {
      url,
      method: HTTP_METHODS.GET,
      params: {
        vendor_id: data
      }
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchSASFDDetails(data) {
    const url = `${URLS.SAS_FD_DETAILS_FETCH_URL}`;
    const config = {
      url,
      method: HTTP_METHODS.GET,
      params: {
        vendor_id: data
      }
    };
    const response = await axios(config);
    return response.data;
  }

  static async updateSASValueDetails(data) {
    const config = {
      url: `${URLS.VENDOR_SAS_VALUE_UPDATE_URL}`,
      method: HTTP_METHODS.PUT,
      data
    };
    const response = await axios(config);
    return response.data;
  }

  static async addSASValueDetails(data) {
    const config = {
      url: `${URLS.VENDOR_SAS_VALUE_ADD_URL}`,
      method: HTTP_METHODS.POST,
      data
    };
    const response = await axios(config);
    return response.data;
  }

  static async importSASFDFile(params) {
    const { file } = params;
    try {
      const signedUrlResponse = await BFFProxy.getSignedUrlToUpload(FILE_TYPES.SAS_FD_REQUEST, file.name);
      const s3UploadResponse = await BFFProxy.uploadWithSignedUrl(signedUrlResponse.data.url, file, file.type);
      if (s3UploadResponse.status === HTTP_STATUS.OK) {
        const data = {
          filePath: `${signedUrlResponse.data.path}/${signedUrlResponse.data.fileName}`,
          filename: encodeURIComponent(signedUrlResponse.data.fileName),
          actualFileName: file.name
        };
        const config = {
          url: `${URLS.SAS_FD_FILE_IMPORT_URL}`,
          method: HTTP_METHODS.POST,
          data
        };
        const response = await axiosWithPayloadInterceptor(config);
        return response.data;
      }
      return Promise.reject(new Error(S3_FILE_UPLOAD_ERROR));
    } catch (e) {
      return Promise.reject(new Error(e.message));
    }
  }

  static async updateSasFdDetails(data) {
    const config = {
      url: `${URLS.ITEM_SAS_FD_DETAILS_UPDATE_URL}`,
      method: HTTP_METHODS.PUT,
      data
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchAgreementNumbers(data) {
    const config = {
      url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}/ids/search`,
      method: HTTP_METHODS.GET,
      params: data
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchAgreementDetails({ agreementId, version }) {
    const config = {
      url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}/${agreementId}`,
      method: HTTP_METHODS.GET,
      params: {
        ...(version && { version })
      }
    };

    const response = await axios(config);
    return response.data;
  }

  static async fetchAgreementLegacyReference({ agreementId }) {
    const config = {
      url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}/${agreementId}/legacy-reference`,
      method: HTTP_METHODS.GET
    };

    const response = await axios(config);
    return response.data;
  }

  static async updateAgreement(data) {
    const {
      bump_item_group_version: bumpItemGroupVersion,
      skip_business_center_validation: skipBusinessCenterValidation
    } = data;
    const config = {
      url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}/${data.agreementId}`,
      method: HTTP_METHODS.PUT,
      params: {
        ...(bumpItemGroupVersion && { bump_item_group_version: bumpItemGroupVersion }),
        ...(skipBusinessCenterValidation && { skip_business_center_validation: skipBusinessCenterValidation })
      },
      data: data.agreementDetails
    };
    const response = await axios(config);
    return response.data;
  }

  static async createAgreement(data) {
    const config = {
      url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}`,
      method: HTTP_METHODS.POST,
      data
    };
    const response = await axios(config);
    return response.data;
  }

  static async cloneAgreement(data) {
    const config = {
      url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}/clone`,
      method: HTTP_METHODS.POST,
      data
    };
    const response = await axios(config);
    return response.data;
  }

  static async declineRecalculation(data) {
    const { agreementId, ...rest } = data;
    const config = {
      url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}/${data.agreementId}`,
      method: HTTP_METHODS.PUT,
      data: { ...rest }
    };
    const response = await axios(config);
    return response.data;
  }

  static async recalculateAgreement(data) {
    try {
      const { agreementId, comment } = data;
      const config = {
        url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}/${agreementId}/re-calculate`,
        method: HTTP_METHODS.POST,
        data: { comment }
      };
      const response = await axios(config);
      return response.data;
    } catch (e) {
      return Promise.reject(new Error(UNABLE_TO_PERSIST_RECALCULATION_COMMENT));
    }
  }

  static async deleteAgreement(data) {
    const config = {
      url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}/${data.agreementId}`,
      method: HTTP_METHODS.DELETE,
      data
    };
    const response = await axios(config);
    return response.data;
  }

  static async uploadAgreementCommentAttachments(attachments) {
    const promises = [];
    const requestId = uuidv4().toString();
    attachments.forEach((attachment) => {
      promises.push(AgreementProxy.uploadAttachments(REQUEST_TYPES.SUPPLIER_SUPPORT_REQUEST, requestId, attachment));
    });
    return Promise.all(promises);
  }

  static handleError(errorMessage) {
    return Promise.reject(new Error(errorMessage));
  }

  static async uploadAttachments(type, requestId, attachment) {
    try {
      const file = attachment;
      const uploadedAttachment = {};
      const signedUrlResponse = await BFFProxy.getSignedUrlToUpload(type, file.name, { subFileType: uploadedAttachment.type, requestId });
      const s3UploadResponse = await BFFProxy.uploadWithSignedUrl(signedUrlResponse.data.url, file, file.type);
      if (s3UploadResponse.status === HTTP_STATUS.OK) {
        uploadedAttachment.directory = signedUrlResponse.data.path;
        uploadedAttachment.actualFileName = signedUrlResponse.data.fileName;
        uploadedAttachment.displayFileName = file.name;
        return uploadedAttachment;
      }
      return this.handleError(S3_FILE_UPLOAD_ERROR);
    } catch (e) {
      return this.handleError(e.message);
    }
  }

  static async fetchAgreementAssociatedVendors(data) {
    const url = `${URLS.AGREEMENT_ASSOCIATED_VENDORS_FETCH_URL}`;
    const config = {
      url,
      method: HTTP_METHODS.GET,
      params: data
    };
    const response = await axios(config);
    return response.data;
  }

  static async fetchItemGroups({ agreementId, version }) {
    const config = {
      url: `${URLS.ITEM_GROUPS_MAINTENANCE_ENDPOINT_URL}`,
      method: HTTP_METHODS.GET,
      params: {
        agreementId,
        ...(version && { version })
      }
    };

    const response = await axios(config);
    return response.data;
  }

  static async fetchItemGroup({ itemGroupId, version }) {
    const config = {
      url: `${URLS.ITEM_GROUPS_MAINTENANCE_ENDPOINT_URL}/${itemGroupId}`,
      method: HTTP_METHODS.GET,
      params: {
        ...(version && { version })
      }
    };

    const response = await axiosWithPayloadInterceptor(config);
    return response.data;
  }

  static async fetchItemGroupLegacyReference(data) {
    const config = {
      url: `${URLS.ITEM_GROUPS_MAINTENANCE_ENDPOINT_URL}/${data}/legacy-reference`,
      method: HTTP_METHODS.GET
    };

    const response = await axios(config);
    return response.data;
  }

  static async deleteItemGroup(data) {
    const { itemGroup, persist_snapshot_only: persistSnapshotOnly, ...rest } = data;

    const config = {
      url: `${URLS.ITEM_GROUPS_MAINTENANCE_ENDPOINT_URL}/${itemGroup}`,
      method: HTTP_METHODS.DELETE,
      params: {
        ...(persistSnapshotOnly && { persist_snapshot_only: persistSnapshotOnly })
      },
      data: rest
    };

    const response = await axios(config);
    return response.data;
  }

  static async fetchVendorItems(data) {
    const config = {
      url: `${URLS.ITEM_GROUPS_MAINTENANCE_ENDPOINT_URL}/vendor-items`,
      method: HTTP_METHODS.GET,
      params: {
        agreementId: data
      }
    };
    const response = await axiosWithPayloadInterceptor(config);
    return response.data;
  }

  static async updateItemGroup(data) {
    const { itemGroup, agreementId, ...rest } = data;
    let dynamicUrl = '';
    if (Object.hasOwn(rest, 'addItems') || Object.hasOwn(rest, 'deleteItems')) {
      dynamicUrl = `${URLS.ITEM_GROUPS_MAINTENANCE_ENDPOINT_URL}/${itemGroup}/items`;
    } else {
      dynamicUrl = `${URLS.ITEM_GROUPS_MAINTENANCE_ENDPOINT_URL}/${itemGroup}`;
    }

    const config = {
      url: dynamicUrl,
      method: HTTP_METHODS.PUT,
      params: {
        agreementId
      },
      data: rest
    };

    const response = await axiosWithPayloadInterceptor(config);
    return response.data;
  }

  static async createItemGroups(data) {
    const { agreementId } = data;
    const config = {
      url: `${URLS.ITEM_GROUPS_MAINTENANCE_ENDPOINT_URL}`,
      method: HTTP_METHODS.POST,
      params: {
        agreementId
      },
      data
    };

    const response = await axios(config);
    return response.data;
  }

  static async fetchExceptions(data) {
    const { agreementId, ...rest } = data;
    const config = {
      url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}/${agreementId}/exceptions`,
      method: HTTP_METHODS.GET,
      params: rest
    };
    const response = await axios(config);
    return response.data;
  }

  static async saveExceptions(data) {
    const { agreementId, ...rest } = data;
    const config = {
      url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}/${agreementId}/exceptions`,
      method: HTTP_METHODS.POST,
      data: rest
    };

    const response = await axios(config);
    return response.data;
  }

  static async deleteExceptions(data) {
    const { agreementId, ...rest } = data;
    const config = {
      url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}/${agreementId}/exceptions`,
      method: HTTP_METHODS.DELETE,
      data: rest
    };

    const response = await axios(config);
    return response.data;
  }

  static async fetchExceptionCodes(data) {
    const { agreementId, ...rest } = data;
    const config = {
      url: `${URLS.AGREEMENT_MAINTENANCE_ENDPOINT_URL}/${agreementId}/exception-codes`,
      method: HTTP_METHODS.GET,
      params: rest
    };
    const response = await axios(config);
    return response.data;
  }
}
export default AgreementProxy;
